import { ThreadStateEnum } from '@anecdotes/commenting';
import { ChangeDetectionStrategy, Component, HostBinding, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { CommentingFacadeService, ConfirmationModalService, FocusingService, CommentPanelManagerService } from '../../services';
import { VirtualScrollRendererComponent } from '@anecdotes/design-system/rendering';
import { Group, groupBy } from '@anecdotes/utils';
import { Observable, firstValueFrom, switchMap } from 'rxjs';
import { delay, filter, map, take } from 'rxjs/operators';
import {
  CommentEntityNameEnum,
  CommentFilterOptionDefinition,
  ThreadSort,
  ThreadViewModel
} from '../../models';
import { selectedSingleThreadControlNameQueryParam } from '../../constants';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@anecdotes/auth-core';

@Component({
  selector: 'app-commenting-panel',
  templateUrl: './commenting-panel.component.html',
  styleUrls: ['./commenting-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommentingPanelComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private threadSortOption: ThreadSort;

  @ViewChild('renderer', { static: true })
  private renderer: VirtualScrollRendererComponent;

  @HostBinding('class')
  private readonly classes = 'flex flex-col h-full';

  @HostBinding('attr.data-test-id')
  private readonly dataTestId = 'comment-panel';

  resourceType: string;
  threads$: Observable<ThreadViewModel[]>;
  filter$: Observable<ThreadViewModel[]>;
  isSingleThreadDisplayed$: Observable<boolean>;
  resourceIdSelector = this.selectThreadId.bind(this);
  groupIdSelector = this.selectGroupId.bind(this);
  overallCommentsCount$: Observable<number>;
  threadsGroup$: Observable<Group<string, ThreadViewModel>[]>;
  hasUniqueSingleThreadDesign$: Observable<boolean>;
  shouldDisplayBackButton$: Observable<boolean>;

  areActiveDisplayed$: Observable<boolean>;
  areResolvedDisplayed$: Observable<boolean>;
  shouldDisableAddCommentButton$: Observable<boolean>;

  focusedResourceId: string;

  get areCommentsEmpty$(): Observable<boolean> {
    return this.filter$.pipe(map((threads) => !threads?.length));
  }

  get resourceId(): string {
    const queryParams = this.router.routerState.snapshot.root.queryParams;

    return queryParams[selectedSingleThreadControlNameQueryParam] as string;
  }

  constructor(
    private commentPanelManagerService: CommentPanelManagerService,
    private confirmationModalService: ConfirmationModalService,
    private focusingService: FocusingService,
    private commentingFacadeService: CommentingFacadeService,
    private router: Router,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.shouldDisableAddCommentButton$ = this.authService.isAuditorSimulationMode();

    this.areActiveDisplayed$ = this.commentPanelManagerService
      .getSelectedFilterOption()
      .pipe(map((filteredOption: CommentFilterOptionDefinition) => filteredOption?.value === ThreadStateEnum.Active));

    this.areResolvedDisplayed$ = this.commentPanelManagerService
      .getSelectedFilterOption()
      .pipe(map((filteredOption: CommentFilterOptionDefinition) => filteredOption?.value === ThreadStateEnum.Resolved));

    this.threads$ = this.commentPanelManagerService.getCommentingResources();

    this.filter$ = this.commentPanelManagerService.getDisplayedCommentingResources();

    this.threadsGroup$ = this.filter$.pipe(
      map((threads) => (threads ? groupBy(threads, (thread) => thread.resourceDisplayName) : []))
    );

    this.isSingleThreadDisplayed$ = this.commentPanelManagerService
      .getDisplayedSingleThreadResourceId()
      .pipe(map((rid) => !!rid));

    this.hasUniqueSingleThreadDesign$ = this.commentPanelManagerService.hasUniqueSingleThreadDesign();
    this.shouldDisplayBackButton$ = this.commentPanelManagerService.shouldDisplayBackButton();

    this.overallCommentsCount$ = this.commentPanelManagerService.getDisplayedSingleThreadResourceId()
      .pipe(
        switchMap((resourceId: string) => {
          if (resourceId) {
            return this.commentPanelManagerService.getThreadsAndRepliesCountForResource(this.resourceType, resourceId);
          }

          return this.commentPanelManagerService.getCommentsCount();
        }),
      );

    this.commentPanelManagerService
      .getSelectedSortOption()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sortingOption) => (this.threadSortOption = sortingOption));

    this.commentPanelManagerService
      .getResourceToCreateThreadFor()
      .pipe(
        delay(500),
        takeUntilDestroyed(this.destroyRef),
        filter((resourceToCreateThreadFor) => !!resourceToCreateThreadFor)
      )
      .subscribe((resourceToCreateThreadFor) => {
        this.renderer.scrollToId(
          this.createThreadCreationId(resourceToCreateThreadFor.resourceType, resourceToCreateThreadFor.resourceId)
        );
      });

    this.commentPanelManagerService.getResourceType()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resourceType: string) => this.resourceType = resourceType);

    this.focusingService
      .getFocusingStreamForResource(CommentEntityNameEnum.Thread)
      .pipe(delay(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(async (threadId) => {
        this.focusedResourceId = await firstValueFrom(this.commentPanelManagerService.getFocusedResourceId());
        this.renderer.scrollToId(threadId);
        this.focusingService.finishFocusing(CommentEntityNameEnum.Thread);
      });

    this.router.routerState.root.queryParams
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((queryParams) => {
        const resourceId: string = queryParams[selectedSingleThreadControlNameQueryParam];

        if (resourceId) {
          this.commentPanelManagerService.displaySingleThread(this.resourceType, resourceId);
        }
      });
  }

  async resolveAll(): Promise<void> {
    const areCommentsEmpty = await firstValueFrom(this.areCommentsEmpty$);

    if (!areCommentsEmpty) {
      const areThreadsShouldBeResolved = await this.confirmationModalService.openResolveAllConfirmation();

      if (areThreadsShouldBeResolved) {
        const isSingleThreadDisplayed = await firstValueFrom(this.isSingleThreadDisplayed$);

        if (isSingleThreadDisplayed) {
          await this.commentingFacadeService.resolveAllActiveThreadsOfResource(this.resourceId, this.resourceType);
        } else {
          await this.commentingFacadeService.resolveAllActiveThreads();
        }
      }
    }
  }

  addNewComment(): void {
    this.commentPanelManagerService.createThread(this.resourceType, this.resourceId || this.focusedResourceId);
  }

  closePanel(): void {
    this.commentPanelManagerService.close();
  }

  buildTranslationKey(relativeKey: string): string {
    return `commenting.${relativeKey}`;
  }

  private selectThreadId(resource: ThreadViewModel): string {
    if (resource.isCreation) {
      return this.createThreadCreationId(resource.resourceType, resource.resourceId);
    }

    if (this.threadSortOption?.field) {
      return `${resource.threadId}-${this.threadSortOption.field}-${this.threadSortOption.direction}`;
    }

    return resource.threadId;
  }

  private selectGroupId(resource: Group<string, ThreadViewModel[]>): string {
    if (this.threadSortOption?.field) {
      return `${resource.key}-${this.threadSortOption.field}-${this.threadSortOption.direction}`;
    }

    return `group_${resource.key}`;
  }

  private createThreadCreationId(resourceType: string, resourceId: string): string {
    return `creation_${this.createTrackingResourceId(resourceType, resourceId)}`;
  }

  private createTrackingResourceId(resourceType: string, resourceId: string): string {
    return `${resourceType}_${resourceId}`;
  }
}
